"use client";

import { useEffect, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_PRIMARY, HINT } from "@/components/tool-kit/styles";
import {
  type Exporters,
  type FormattedWork,
  PublicationList,
  WorksTable,
} from "@/components/tools/reference-tools/orcid-parts";
import {
  type RunProgress,
  useRun,
} from "@/components/tools/reference-tools/use-run";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { formatEntries } from "@/lib/restyle";
import type { FetchFailure } from "@/lib/tool-kit/reference-tools/network";
import {
  checkOrcid,
  type OrcidCheck,
  type OrcidWork,
} from "@/lib/tool-kit/reference-tools/orcid";
import {
  buildSources,
  fetchProfile,
  type OrcidProfile,
  type WorkSource,
} from "@/lib/tool-kit/reference-tools/orcid-run";

/** English strings. A translated content file overrides these through `ui`. */
export const ORCID_PUBLICATION_LIST_STRINGS = {
  idLabel: "ORCID iD",
  idPlaceholder: "0000-0002-1825-0097",
  idHint:
    "Sixteen characters in four groups, or the full https://orcid.org/ link. The last character may be X.",
  invalidFormat:
    "An ORCID iD has sixteen characters in four groups of four, such as 0000-0002-1825-0097.",
  invalidChecksum:
    "The last character should be {expected}, not {found} — a digit has probably been mistyped.",
  fetch: "Fetch works",
  fetching: "Fetching the record…",
  fetchFailed: {
    "not-found": "There is no public ORCID record with this iD.",
    "rate-limited": "ORCID is limiting requests. Wait a minute and try again.",
    unavailable: "ORCID did not answer properly. Try again shortly.",
    timeout: "ORCID took too long to answer. Try again.",
    network: "ORCID could not be reached — check your connection.",
  },
  emptyPrompt: "Enter an ORCID iD and fetch its public works.",
  noWorks: "This record lists no public works.",
  worksHeading: "Works on the record of {owner}",
  worksHeadingAnonymous: "Works on this record",
  worksCount:
    "one:{count} public work, newest first|other:{count} public works, newest first",
  selectAll: "Select all",
  selectNone: "Select none",
  selected: "{selected} selected — up to {max} per list",
  columns: {
    include: "Include",
    year: "Year",
    type: "Type",
    title: "Title",
    doi: "DOI",
  },
  noDate: "—",
  types: {
    "journal-article": "Journal article",
    book: "Book",
    "edited-book": "Edited book",
    "book-chapter": "Book chapter",
    "conference-paper": "Conference paper",
    "conference-abstract": "Conference abstract",
    "conference-poster": "Conference poster",
    "dissertation-thesis": "Dissertation or thesis",
    report: "Report",
    "working-paper": "Working paper",
    preprint: "Preprint",
    "data-set": "Data set",
    software: "Software",
    patent: "Patent",
    review: "Review",
    "book-review": "Book review",
    "magazine-article": "Magazine article",
    "newspaper-article": "Newspaper article",
    "encyclopedia-entry": "Encyclopedia entry",
    "lecture-speech": "Lecture or speech",
    website: "Website",
    other: "Other",
  },
  styleLabel: "Citation style",
  styles: {
    apa: "APA 7",
    "modern-language-association": "MLA 9",
    "chicago-author-date": "Chicago author-date",
    "harvard-cite-them-right": "Harvard (Cite Them Right)",
    vancouver: "Vancouver",
    ieee: "IEEE",
  },
  numbered: "Number the list",
  format: "Format the selected works",
  lookingUp: "Looking up the works: {done} of {total} requests…",
  formatting: "Formatting: {done} of {total} works…",
  formatFailed:
    "The works could not be formatted — CitationEasy's citation service did not answer. Try again.",
  resultsHeading: "Your publication list",
  formattedCount:
    "one:{count} work in {style}, newest first|other:{count} works in {style}, newest first",
  fromDoi: "From the DOI record",
  fromOrcid: "From ORCID's metadata",
  ownerOnly:
    "ORCID lists no authors for this work, so only {owner} is shown — add the co-authors before you use it.",
  notFormatted: "This work could not be formatted.",
  copyText: "Copy as text",
  copyHtml: "Copy with hanging indents",
  copied: "Copied!",
  downloadBib: "Download .bib",
  downloadRis: "Download .ris",
  loadingExports: "Preparing the copy and download options…",
  exportsFailed:
    "The copy and download options could not be loaded. Select the list on the page and copy it instead.",
  servicesNote:
    "Your browser reads the record from ORCID's public API (pub.orcid.org). Works with a DOI are looked up, and every work is formatted, by CitationEasy's citation service.",
  italicsNote:
    "Copies are plain text, like the site's other exports — set journal and book titles in italics if your CV's style needs them.",
} as const;

export type OrcidStrings = typeof ORCID_PUBLICATION_LIST_STRINGS;
type StyleSlug = keyof OrcidStrings["styles"];

const MAX_WORKS = 100;
/**
 * Numbered styles number each reference by its place in a document. Each work here is
 * formatted alone, so each would come back as [1] or 1.; the marker is dropped and the
 * list's own numbering option takes its place.
 */
const NUMBERED_STYLES = new Set<string>(["ieee", "vancouver"]);
const RE_NUMBER_MARKER = /^\s*(?:\[\d+\]|\d+\.)\s+/;
const STYLE_SLUGS = Object.keys(
  ORCID_PUBLICATION_LIST_STRINGS.styles
) as StyleSlug[];

/** What is wrong with a typed iD, once the reader has asked to fetch it. */
function IdProblem({
  check,
  strings,
}: {
  check: OrcidCheck;
  strings: OrcidStrings;
}) {
  if (check.ok || check.reason === "empty") {
    return null;
  }
  if (check.reason === "checksum") {
    return (
      <ErrorNotice>
        {t(strings.invalidChecksum, {
          expected: check.expected,
          found: check.found,
        })}
      </ErrorNotice>
    );
  }
  return <ErrorNotice>{strings.invalidFormat}</ErrorNotice>;
}

/** The format button's label: idle, or the phase and progress of a run. */
function formatLabel(
  progress: RunProgress | null,
  phase: "lookup" | "format",
  strings: OrcidStrings,
  fmt: (value: number) => string
): string {
  if (!progress) {
    return strings.format;
  }
  return t(phase === "lookup" ? strings.lookingUp : strings.formatting, {
    done: fmt(progress.done),
    total: fmt(progress.total),
  });
}

/** The year a reference shows: its record's own date, else ORCID's. */
function shownYear(source: WorkSource | undefined, work: OrcidWork): number {
  const year = Number.parseInt((source?.entry.issued ?? "").slice(0, 4), 10);
  return Number.isFinite(year) ? year : (work.year ?? -1);
}

function withoutMarker(citation: string | null, style: string): string | null {
  if (citation === null) {
    return null;
  }
  const text = citation.trim();
  return NUMBERED_STYLES.has(style) ? text.replace(RE_NUMBER_MARKER, "") : text;
}

/** Formatted works, newest first by the year each reference shows. */
function orderedResults(
  works: readonly OrcidWork[],
  built: ReadonlyMap<number, WorkSource>,
  citations: readonly (string | null)[],
  style: string
): FormattedWork[] {
  return works
    .map((work, index) => ({
      work,
      source: built.get(work.putCode) as WorkSource,
      citation: withoutMarker(citations[index] ?? null, style),
      style,
      year: shownYear(built.get(work.putCode), work),
      index,
    }))
    .sort((a, b) => b.year - a.year || a.index - b.index)
    .map(({ work, source, citation }) => ({ work, source, citation, style }));
}

export default function OrcidPublicationListWidget() {
  const strings = useToolUi(ORCID_PUBLICATION_LIST_STRINGS);
  const fmt = useToolNumber();
  const [idInput, setIdInput] = useState("");
  const [showCheck, setShowCheck] = useState(false);
  const [profile, setProfile] = useState<OrcidProfile | null>(null);
  const [fetchError, setFetchError] = useState<FetchFailure | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [style, setStyle] = useState<StyleSlug>("apa");
  const [numbered, setNumbered] = useState(false);
  const [sources, setSources] = useState<Map<number, WorkSource>>(new Map());
  const [results, setResults] = useState<FormattedWork[]>([]);
  const [phase, setPhase] = useState<"lookup" | "format">("lookup");
  const [formatFailed, setFormatFailed] = useState(false);
  const [exporters, setExporters] = useState<Exporters | null>(null);
  const [exportsFailed, setExportsFailed] = useState(false);
  const fetchRun = useRun();
  const formatRun = useRun();

  const check = checkOrcid(idInput);

  useEffect(() => {
    if (results.length === 0 || exporters) {
      return;
    }
    let cancelled = false;
    Promise.all([import("@/lib/bibliography"), import("@/lib/citation")])
      .then(([bibliography, citation]) => {
        if (!cancelled) {
          setExporters({
            exportAsText: bibliography.exportAsText,
            exportAsHtml: bibliography.exportAsHtml,
            exportAsRis: bibliography.exportAsRis,
            editableEntryToCsl: citation.editableEntryToCsl,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setExportsFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [results, exporters]);

  async function handleFetch() {
    setShowCheck(true);
    if (!check.ok) {
      return;
    }
    const controller = fetchRun.start(1);
    setFetchError(null);
    try {
      const result = await fetchProfile(check.id, controller.signal);
      if (!fetchRun.isCurrent(controller)) {
        return;
      }
      if (!result.ok) {
        setFetchError(result.reason);
        return;
      }
      setProfile(result.profile);
      setSelected(
        new Set(
          result.profile.works.slice(0, MAX_WORKS).map((work) => work.putCode)
        )
      );
      setSources(new Map());
      setResults([]);
    } catch {
      if (fetchRun.isCurrent(controller)) {
        setFetchError("network");
      }
    } finally {
      fetchRun.finish(controller);
    }
  }

  async function handleFormat() {
    if (!profile) {
      return;
    }
    const works = profile.works
      .filter((work) => selected.has(work.putCode))
      .slice(0, MAX_WORKS);
    const controller = formatRun.start(1);
    setPhase("lookup");
    setFormatFailed(false);
    try {
      const built = await buildSources(
        profile,
        works,
        sources,
        controller.signal,
        ({ done, total }) => formatRun.advance(controller, done, total)
      );
      if (!(built && formatRun.isCurrent(controller))) {
        return;
      }
      setSources(built);
      setPhase("format");
      formatRun.advance(controller, 0, works.length);
      const entries = works.map((work) => built.get(work.putCode)?.entry ?? {});
      const citations = await formatEntries(entries, style, {
        signal: controller.signal,
        onProgress: (done, total) => formatRun.advance(controller, done, total),
      });
      if (formatRun.isCurrent(controller)) {
        setFormatFailed(citations.every((citation) => citation === null));
        setResults(orderedResults(works, built, citations, style));
      }
    } catch {
      if (formatRun.isCurrent(controller)) {
        setFormatFailed(true);
      }
    } finally {
      formatRun.finish(controller);
    }
  }

  return (
    <section className="space-y-6">
      <div className="space-y-3">
        <TextField
          hint={strings.idHint}
          id="opl-id"
          label={strings.idLabel}
          mono
          onChange={(value) => {
            setIdInput(value);
            setShowCheck(false);
          }}
          placeholder={strings.idPlaceholder}
          value={idInput}
        />
        {showCheck ? <IdProblem check={check} strings={strings} /> : null}
        <button
          className={BUTTON_PRIMARY}
          disabled={fetchRun.running || idInput.trim() === ""}
          onClick={handleFetch}
          type="button"
        >
          {fetchRun.running ? strings.fetching : strings.fetch}
        </button>
      </div>
      <div aria-live="polite" className="space-y-6">
        {fetchError ? (
          <ErrorNotice>{strings.fetchFailed[fetchError]}</ErrorNotice>
        ) : null}
        {profile === null && fetchError === null ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : null}
        {profile && profile.works.length === 0 ? (
          <Notice>{strings.noWorks}</Notice>
        ) : null}
        {profile && profile.works.length > 0 ? (
          <>
            <WorksTable
              max={MAX_WORKS}
              onSelect={setSelected}
              profile={profile}
              selected={selected}
              strings={strings}
            />
            <div className="grid items-end gap-4 sm:grid-cols-2">
              <SelectField<StyleSlug>
                id="opl-style"
                label={strings.styleLabel}
                onChange={setStyle}
                options={STYLE_SLUGS.map((slug) => ({
                  value: slug,
                  label: strings.styles[slug],
                }))}
                value={style}
              />
              <Checkbox
                checked={numbered}
                label={strings.numbered}
                onChange={setNumbered}
              />
            </div>
            <button
              className={BUTTON_PRIMARY}
              disabled={formatRun.running || selected.size === 0}
              onClick={handleFormat}
              type="button"
            >
              {formatLabel(formatRun.progress, phase, strings, fmt)}
            </button>
          </>
        ) : null}
        {formatFailed ? (
          <ErrorNotice>{strings.formatFailed}</ErrorNotice>
        ) : null}
        {results.length > 0 && profile ? (
          <PublicationList
            exporters={exporters}
            exportsFailed={exportsFailed}
            numbered={numbered}
            owner={profile.owner}
            results={results}
            strings={strings}
          />
        ) : null}
      </div>
      <p className={HINT}>{strings.servicesNote}</p>
    </section>
  );
}
